import { create } from "zustand";

interface Account {
  id: string;
  email: string;
  fullName: string;
  role: string;
  status: string;
}

interface Notification {
  id: string;
  type: "success" | "error" | "info" | "warning";
  message: string;
  duration?: number;
}

interface AuthState {
  account: Account | null;
  loading: boolean;
  error: string | null;
  setAccount: (account: Account | null) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  account: null,
  loading: false,
  error: null,
  setAccount: (account) => set({ account, error: null }),
  logout: () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    set({ account: null });
  },
}));

// Decode JWT payload (no verification — just extract claims for UI purposes).
function decodeJwtPayload(token: string): Record<string, any> | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

// hydrateAccountFromToken populates the auth store from the access token's JWT
// claims. Use after storing a fresh token (login) or on app boot.
export function hydrateAccountFromToken(): void {
  if (typeof window === "undefined") return;
  const token = localStorage.getItem("access_token");
  if (!token) return;
  const claims = decodeJwtPayload(token);
  if (!claims?.sub) return;

  useAuthStore.getState().setAccount({
    id: claims.sub,
    email: claims.email || "",
    fullName: claims.full_name || "",
    role: claims.role || "user",
    status: "active",
  });
}

interface UiState {
  notifications: Notification[];
  addNotification: (notif: Omit<Notification, "id">) => void;
  removeNotification: (id: string) => void;
}

export const useUiStore = create<UiState>((set) => ({
  notifications: [],
  addNotification: (notif) =>
    set((state) => ({
      notifications: [
        ...state.notifications,
        { ...notif, id: crypto.randomUUID() },
      ],
    })),
  removeNotification: (id) =>
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    })),
}));
